import type { Brand, BrandCreate } from '@erp/shared';
import {
  Ban,
  Image as ImageIcon,
  Pencil,
  RotateCcw,
  Tag,
  Trash2,
  Upload,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Card, Input, Skeleton } from '../../components/shadcn/index.js';
import {
  Badge,
  Button,
  confirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
} from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import {
  deleteAttachment,
  fetchAttachmentObjectUrl,
  isImageMime,
  listAttachments,
  uploadAttachment,
} from '../../lib/attachments.js';
import { ImageEditorDialog } from '../common/ImageEditorDialog.js';

export function BrandsPage(): JSX.Element {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('items.write');
  const canFiles = usePermission('attachments.read');
  const [brands, setBrands] = useState<Brand[] | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [photoVersion, setPhotoVersion] = useState(0);
  const [itemCounts, setItemCounts] = useState<Map<string, number>>(new Map());

  const reload = async (): Promise<void> => {
    const r = await api().get<{ items: Brand[] }>('/brands');
    setBrands(r.data.items);
  };
  useEffect(() => {
    void reload();
    // Item counts per brand (client-side tally; the count links open the
    // items list filtered to that brand).
    void api()
      .get<{ items: Array<{ brandId?: string | null }> }>('/items')
      .then((r) => {
        const m = new Map<string, number>();
        for (const it of r.data.items) if (it.brandId) m.set(it.brandId, (m.get(it.brandId) ?? 0) + 1);
        setItemCounts(m);
      })
      .catch(() => undefined);
  }, []);

  const editing = editingId && brands ? brands.find((b) => b.id === editingId) ?? null : null;

  if (!brands) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-32 w-full" />
      </Card>
    );
  }

  const onDeactivate = async (b: Brand): Promise<void> => {
    if (!(await confirmDialog({ message: t('brands.actions.confirmDeactivate', { title: b.title, defaultValue: `Deactivate ${b.title}?` }), tone: 'danger' }))) return;
    try {
      await api().delete(`/brands/${b.id}`);
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };
  const onReactivate = async (b: Brand): Promise<void> => {
    try {
      await api().patch(`/brands/${b.id}`, { isActive: true });
      await reload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  const columns: DataTableColumn<Brand>[] = [
    {
      id: 'logo', header: '', headClassName: 'w-14',
      cell: (b) => <BrandThumb brand={b} version={photoVersion} enabled={canFiles} />,
    },
    {
      id: 'title', header: t('brands.columns.title', { defaultValue: 'Brand' }),
      sortValue: (b) => b.title, searchValue: (b) => b.title,
      cell: (b) => <span className="font-semibold">{b.title}</span>,
    },
    {
      id: 'items', header: t('brands.columns.items', { defaultValue: 'Items' }), align: 'end',
      sortValue: (b) => itemCounts.get(b.id) ?? 0,
      cell: (b) => {
        const n = itemCounts.get(b.id) ?? 0;
        return n > 0 ? (
          <Link
            to={`/inventory/items?brand=${b.id}`}
            onClick={(e) => e.stopPropagation()}
            className="font-mono font-semibold tabular-nums text-brand underline-offset-4 hover:underline"
          >
            {n}
          </Link>
        ) : (
          <span className="font-mono tabular-nums text-fg-subtle">0</span>
        );
      },
    },
    {
      id: 'sort', header: t('brands.columns.sort', { defaultValue: 'Sort' }), align: 'end',
      sortValue: (b) => b.sort, cellClassName: 'font-mono tabular-nums text-fg-muted',
      cell: (b) => b.sort,
    },
    {
      id: 'status', header: t('brands.columns.status', { defaultValue: 'Status' }),
      sortValue: (b) => (b.isActive ? 1 : 0),
      cell: (b) => (
        <Badge tone={b.isActive ? 'success' : 'neutral'}>
          {t(`status.${b.isActive ? 'active' : 'inactive'}`, { defaultValue: b.isActive ? 'Active' : 'Inactive' })}
        </Badge>
      ),
    },
    ...(canWrite
      ? [{
          id: 'actions', header: '', align: 'end' as const, headClassName: 'w-28',
          cell: (b: Brand) => (
            <div className="flex justify-end gap-1">
              <Button variant="ghost" size="sm" onClick={() => { setShowCreate(false); setEditingId(b.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }} aria-label={`${tc('actions.edit')} ${b.title}`} title={tc('actions.edit')}>
                <Pencil className="h-4 w-4" aria-hidden />
              </Button>
              {b.isActive ? (
                <Button variant="ghost" size="sm" onClick={() => void onDeactivate(b)} aria-label={`${t('brands.actions.deactivate', { defaultValue: 'Deactivate' })} ${b.title}`} title={t('brands.actions.deactivate', { defaultValue: 'Deactivate' })}>
                  <Ban className="h-4 w-4" aria-hidden />
                </Button>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => void onReactivate(b)} aria-label={`${t('brands.actions.reactivate', { defaultValue: 'Reactivate' })} ${b.title}`} title={t('brands.actions.reactivate', { defaultValue: 'Reactivate' })}>
                  <RotateCcw className="h-4 w-4" aria-hidden />
                </Button>
              )}
            </div>
          ),
        }]
      : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('brands.title', { defaultValue: 'Brands' })}</h1>
          <p className="mt-1 text-sm text-fg-muted">
            {t('brands.description', { defaultValue: 'Product brands / manufacturers. Give each a logo and link items to it.' })}
          </p>
        </div>
        {canWrite && !showCreate && !editingId && (
          <Button onClick={() => setShowCreate(true)}>
            {t('brands.create.title', { defaultValue: 'Add brand' })}
          </Button>
        )}
      </header>

      {showCreate && (
        <BrandForm
          onCancel={() => setShowCreate(false)}
          onSaved={async () => {
            setShowCreate(false);
            setPhotoVersion((v) => v + 1);
            await reload();
          }}
        />
      )}
      {editing && (
        <BrandForm
          initial={editing}
          onCancel={() => setEditingId(null)}
          onSaved={async () => {
            setEditingId(null);
            setPhotoVersion((v) => v + 1);
            await reload();
          }}
        />
      )}

      {brands.length === 0 && !showCreate ? (
        <EmptyState
          icon={<Tag className="h-8 w-8" aria-hidden />}
          title={t('brands.empty', { defaultValue: 'No brands yet' })}
          description={t('brands.emptyDescription', {
            defaultValue: 'Add the brands or manufacturers you carry, give each a logo, then link items to them.',
          })}
          action={
            canWrite ? (
              <Button size="sm" onClick={() => setShowCreate(true)}>
                {t('brands.create.title', { defaultValue: 'Add brand' })}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable
            columns={columns}
            data={brands}
            rowKey={(b) => b.id}
            rowClassName={(b) => (b.isActive ? undefined : 'opacity-60')}
            searchPlaceholder={t('brands.searchPlaceholder', { defaultValue: 'Search brand…' })}
          />
        </Card>
      )}
    </div>
  );
}

/** 36px brand logo thumbnail — first image attachment, tag fallback. */
function BrandThumb({ brand, version, enabled }: { brand: Brand; version: number; enabled: boolean }): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    let made: string | null = null;
    void (async () => {
      try {
        const list = await listAttachments('brand', brand.id);
        const img = list.filter((a) => isImageMime(a.mimeType)).sort((a, b) => a.uploadedAt.localeCompare(b.uploadedAt))[0];
        if (!img) { if (active) setUrl(null); return; }
        const u = await fetchAttachmentObjectUrl(img.id);
        if (active) { made = u; setUrl(u); } else URL.revokeObjectURL(u);
      } catch { if (active) setUrl(null); }
    })();
    return () => { active = false; if (made) URL.revokeObjectURL(made); };
  }, [brand.id, version, enabled]);
  return (
    <div className="grid h-9 w-9 flex-none place-items-center overflow-hidden rounded-lg border border-border bg-bg-subtle">
      {url ? <img src={url} alt="" className="h-full w-full object-contain" /> : <Tag className="h-[18px] w-[18px] text-fg-muted" aria-hidden />}
    </div>
  );
}

interface BrandFormProps {
  initial?: Brand;
  onCancel: () => void;
  onSaved: () => void | Promise<void>;
}

function BrandForm({ initial, onCancel, onSaved }: BrandFormProps): JSX.Element {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation();
  const isEdit = !!initial;
  const canFiles = usePermission('attachments.write');
  const [form, setForm] = useState<BrandCreate>(() =>
    initial ? { title: initial.title, sort: initial.sort } : { title: '', sort: 0 },
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Logo (single image attachment, entityType='brand').
  const imgInputRef = useRef<HTMLInputElement>(null);
  const objUrlRef = useRef<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [stagedBlob, setStagedBlob] = useState<Blob | null>(null);
  const [existingImageId, setExistingImageId] = useState<string | null>(null);
  const [removeExisting, setRemoveExisting] = useState(false);
  const [editorFile, setEditorFile] = useState<File | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);

  const showImage = useCallback((next: string | null): void => {
    if (objUrlRef.current) URL.revokeObjectURL(objUrlRef.current);
    objUrlRef.current = next;
    setImageUrl(next);
  }, []);

  useEffect(() => {
    if (!isEdit || !initial || !canFiles) return undefined;
    let active = true;
    void (async () => {
      try {
        const list = await listAttachments('brand', initial.id);
        const img = list.filter((a) => isImageMime(a.mimeType)).sort((a, b) => a.uploadedAt.localeCompare(b.uploadedAt))[0];
        if (!img || !active) return;
        setExistingImageId(img.id);
        const u = await fetchAttachmentObjectUrl(img.id);
        if (active) showImage(u); else URL.revokeObjectURL(u);
      } catch { /* no logo */ }
    })();
    return () => { active = false; };
  }, [isEdit, initial, canFiles, showImage]);

  useEffect(() => () => { if (objUrlRef.current) URL.revokeObjectURL(objUrlRef.current); }, []);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    let brandId: string | undefined;
    try {
      if (isEdit && initial) {
        await api().patch(`/brands/${initial.id}`, { title: form.title.trim(), sort: form.sort ?? 0 });
        brandId = initial.id;
      } else {
        const created = await api().post<Brand>('/brands', { title: form.title.trim(), sort: form.sort ?? 0 });
        brandId = created.data.id;
      }
      if (brandId && canFiles && (stagedBlob || removeExisting)) {
        try {
          if (existingImageId && (removeExisting || stagedBlob)) await deleteAttachment(existingImageId);
          if (stagedBlob) await uploadAttachment('brand', brandId, new File([stagedBlob], 'brand-logo.jpg', { type: 'image/jpeg' }));
        } catch { /* logo non-blocking */ }
      }
      await onSaved();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      if (code === 'conflict') setError(t('brands.errors.duplicateTitle', { defaultValue: 'That brand name is already in use.' }));
      else setError(tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6">
      <h2 className="mb-4 text-lg font-semibold">
        {isEdit ? t('brands.edit.title', { name: initial?.title, defaultValue: `Edit ${initial?.title}` }) : t('brands.create.title', { defaultValue: 'Add brand' })}
      </h2>
      <form className="grid grid-cols-1 gap-4 md:grid-cols-2" onSubmit={onSubmit} noValidate>
        {canFiles && (
          <div className="md:col-span-2 flex items-center gap-4">
            <div className="grid h-20 w-20 flex-none place-items-center overflow-hidden rounded-xl border border-border bg-bg-subtle">
              {imageUrl ? <img src={imageUrl} alt="" className="h-full w-full object-contain" /> : <ImageIcon className="h-7 w-7 text-fg-muted" aria-hidden />}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-fg">{t('brands.create.photo', { defaultValue: 'Brand logo' })}</div>
              <p className="mt-0.5 text-xs text-fg-muted">{t('brands.create.photoHint', { defaultValue: 'Crop & rotate — compressed automatically.' })}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <input ref={imgInputRef} type="file" accept="image/*" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) { setEditorFile(f); setEditorOpen(true); } if (e.target) e.target.value = ''; }} />
                <Button type="button" variant="secondary" size="sm" onClick={() => imgInputRef.current?.click()}>
                  <Upload className="h-4 w-4" aria-hidden />
                  {imageUrl ? t('brands.create.changePhoto', { defaultValue: 'Change' }) : t('brands.create.uploadPhoto', { defaultValue: 'Upload logo' })}
                </Button>
                {imageUrl && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => { setStagedBlob(null); showImage(null); if (existingImageId) setRemoveExisting(true); }}>
                    <Trash2 className="h-4 w-4" aria-hidden />
                    {t('brands.create.removePhoto', { defaultValue: 'Remove' })}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
        <FormField id="brand-title" label={t('brands.create.name', { defaultValue: 'Name' })} required>
          <Input id="brand-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required autoFocus maxLength={200} />
        </FormField>
        <FormField id="brand-sort" label={t('brands.create.sort', { defaultValue: 'Sort order' })}>
          <Input id="brand-sort" type="number" value={String(form.sort ?? 0)} onChange={(e) => setForm({ ...form, sort: Number(e.target.value) || 0 })} />
        </FormField>
        {error && <p className="md:col-span-2 text-sm text-danger" role="alert">{error}</p>}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>{isEdit ? tc('actions.save') : t('brands.create.submit', { defaultValue: 'Create brand' })}</Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>{tc('actions.cancel')}</Button>
        </div>
      </form>

      <ImageEditorDialog
        open={editorOpen}
        source={editorFile}
        title={t('brands.create.editPhoto', { defaultValue: 'Edit brand logo' })}
        onCancel={() => { setEditorOpen(false); setEditorFile(null); }}
        onSave={(blob) => { setStagedBlob(blob); setRemoveExisting(false); showImage(URL.createObjectURL(blob)); setEditorOpen(false); setEditorFile(null); }}
      />
    </Card>
  );
}

